import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import useReactApexChart from "../../hook/useReactApexChart";
import { api } from "../../services/api";

const TotalSubscriberOne = () => {
  const [subscriptionData, setSubscriptionData] = useState({
    totalCount: 0,
    growthPercent: 0,
    dailyAverage: 0,
    loading: true,
    error: false,
  });

  // Get the default chart options from hook
  let { barChartOptions } = useReactApexChart();

  // We'll modify the series data with our actual subscription data
  const [chartSeries, setChartSeries] = useState([
    {
      name: "Subscribers",
      data: [30, 40, 35, 50, 49, 60], // Default fallback data
    },
  ]);

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        // Fetch subscription stats
        const response = await api.subscriptions.getStats();

        if (response.success && response.stats) {
          // Extract data from the API response
          const { totalCount, activeCount, monthlyData } = response.stats;

          // Calculate growth percentage and daily average
          let growthPercent = 0;
          let dailyAverage = 0;

          if (monthlyData && monthlyData.length >= 2) {
            const currentMonth = monthlyData[monthlyData.length - 1];
            const previousMonth = monthlyData[monthlyData.length - 2];

            if (previousMonth.count > 0) {
              growthPercent =
                ((currentMonth.count - previousMonth.count) /
                  previousMonth.count) *
                100;
            }

            // Calculate daily average for current month
            const daysInMonth = new Date(
              currentMonth.year,
              currentMonth.month,
              0
            ).getDate();
            dailyAverage = Math.round(currentMonth.count / daysInMonth);
          } else {
            // If no monthly data is available, use current date to estimate
            const currentDate = new Date();
            const dayOfMonth = currentDate.getDate();
            dailyAverage = Math.round(totalCount / dayOfMonth) || 1; // Ensure at least 1
          }

          // Update chart series with monthly data
          if (monthlyData && monthlyData.length > 0) {
            // Use the last 6 months for the chart
            const lastSixMonths = monthlyData.slice(-6);

            setChartSeries([
              {
                name: "New Subscribers",
                data: lastSixMonths.map((month) => month.count),
              },
            ]);
          }

          // Update state with the fetched data
          setSubscriptionData({
            totalCount,
            activeCount,
            growthPercent: Math.round(growthPercent),
            dailyAverage,
            loading: false,
            error: false,
          });
        } else {
          // Handle API response error
          console.error("API returned an error:", response.message);
          setSubscriptionData({
            totalCount: 5000, // Fallback data
            activeCount: 4500,
            growthPercent: 10,
            dailyAverage: 20,
            loading: false,
            error: true,
          });
        }
      } catch (error) {
        console.error("Error fetching subscription data:", error);
        setSubscriptionData({
          totalCount: 5000, // Fallback data
          activeCount: 4500,
          growthPercent: 10,
          dailyAverage: 20,
          loading: false,
          error: true,
        });
      }
    };

    fetchSubscriptionData();
  }, []);

  // Determine if growth is positive or negative for styling
  const isPositiveGrowth = subscriptionData.growthPercent >= 0;
  const growthClass = isPositiveGrowth
    ? "bg-success-focus text-success-main"
    : "bg-danger-focus text-danger-main";
  const growthIcon = isPositiveGrowth
    ? "iconamoon:arrow-up-2-fill"
    : "iconamoon:arrow-down-2-fill";

  return (
    <div className="col-xxl-4 col-xl-8">
      <div className="card h-100 radius-8 border">
        <div className="card-body p-24">
          <h6 className="mb-12 fw-semibold text-lg mb-16">Total Subscribers</h6>
          {subscriptionData.error && (
            <div className="text-sm text-danger-main mb-2">
              <Icon icon="mdi:alert-circle-outline" className="me-1" />
              Using demo data (API error)
            </div>
          )}
          <div className="d-flex align-items-center gap-2 mb-20">
            <h6 className="fw-semibold mb-0">
              {subscriptionData.loading
                ? "Loading..."
                : subscriptionData.totalCount.toLocaleString()}
            </h6>
            <p className="text-sm mb-0">
              <span
                className={`border px-8 py-2 rounded-pill fw-semibold d-inline-flex align-items-center gap-1 ${growthClass}`}
              >
                {Math.abs(subscriptionData.growthPercent)}%
                <Icon icon={growthIcon} className="icon" />
              </span>
              - {subscriptionData.dailyAverage} Per Day
            </p>
          </div>
          <ReactApexChart
            options={barChartOptions}
            series={chartSeries}
            type="bar"
            height={264}
          />
        </div>
      </div>
    </div>
  );
};

export default TotalSubscriberOne;
